import React, { useCallback, useMemo, useState } from 'react';
import { Employee, Branch } from '../../types';
import { PageHeader } from '../../components/Common';
import AsyncSelect from '../../components/Form/AsyncSelect';
import { RaaftFormState, CrewChangeBatch } from './templates';
import { arabicEmployeeName } from '../../utils/employeeName';

interface GeneratorFormProps {
  form: RaaftFormState;
  setForm: React.Dispatch<React.SetStateAction<RaaftFormState>>;
  vessels: Branch[];
  employees: Employee[];
  generatedPreview: string;
  /** Print is saving the generation record — disable the button meanwhile. */
  isPrinting?: boolean;
  autoFilledFromCrewChange?: boolean;
  /** Sign-off batches grouped by change date — picking one selects that crew. */
  batches?: CrewChangeBatch[];
  onGeneratePreview: () => void;
  onPrintLetter: () => void;
  onClose: () => void;
}

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';
const OPTION_PAGE_SIZE = 10;

const RaaftGeneratorForm: React.FC<GeneratorFormProps> = ({
  form, setForm, vessels, employees, generatedPreview, isPrinting = false, autoFilledFromCrewChange, batches = [], onGeneratePreview, onPrintLetter, onClose,
}) => {
  const selectedEmps = employees.filter(e => form.selectedEmployeeIds.includes(e.id));

  // Client-side paged+searched fetchers — AsyncSelect handles debounce,
  // search box and "load more" pagination over these local lists.
  const fetchVesselOptions = useCallback(async (query: string, page: number) => {
    const q = query.trim().toLowerCase();
    const filtered = q ? vessels.filter((v) => (v.name || '').toLowerCase().includes(q)) : vessels;
    return { data: filtered.slice((page - 1) * OPTION_PAGE_SIZE, page * OPTION_PAGE_SIZE),
      meta: { pagination: { page, pageCount: Math.max(1, Math.ceil(filtered.length / OPTION_PAGE_SIZE)) } } };
  }, [vessels]);

  const fetchBatchOptions = useCallback(async (query: string, page: number) => {
    const q = query.trim().toLowerCase();
    const filtered = q ? batches.filter((b) => b.label.toLowerCase().includes(q)) : batches;
    return { data: filtered.slice((page - 1) * OPTION_PAGE_SIZE, page * OPTION_PAGE_SIZE),
      meta: { pagination: { page, pageCount: Math.max(1, Math.ceil(filtered.length / OPTION_PAGE_SIZE)) } } };
  }, [batches]);

  const [empSearch, setEmpSearch] = useState('');
  const visibleEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      arabicEmployeeName(e).toLowerCase().includes(q) ||
      (e.name || '').toLowerCase().includes(q) ||
      (e.nationalId || '').includes(q) ||
      ((e as any).rank || e.position || '').toLowerCase().includes(q));
  }, [employees, empSearch]);

  const selectedBatch = batches.find((b) => b.date === form.batchDate && String(b.vesselId || '') === String(form.vesselId || ''));

  const selectBatch = (batch: CrewChangeBatch) => {
    setForm(prev => ({ ...prev, selectedEmployeeIds: batch.employeeIds, batchDate: batch.date === 'unknown' ? undefined : batch.date, vesselId: batch.vesselId || prev.vesselId }));
  };

  const handleToggleEmployee = (empId: string) => {
    setForm(prev => ({
      ...prev,
      selectedEmployeeIds: prev.selectedEmployeeIds.includes(empId)
        ? prev.selectedEmployeeIds.filter(id => id !== empId)
        : [...prev.selectedEmployeeIds, empId],
    }));
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Immigration Sign Off - Generate Letter" description="طلب رفت بحارة - تأشيرة خروج"
        actions={[{ label: 'Back', onClick: onClose, variant: 'info' }]}
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">Vessel &amp; Letter Info</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">مكتب الجوازات (Passport Office)</label>
            <input type="text" value={form.passportOffice} onChange={e => setForm(prev => ({ ...prev, passportOffice: e.target.value }))} placeholder="بورسعيد" className={inputClass} />
          </div>
          <div>
            <AsyncSelect
              label="Vessel"
              name="vesselId"
              value={form.vesselId}
              onChange={(v) => setForm((p) => ({ ...p, vesselId: v }))}
              fetchOptions={fetchVesselOptions}
              getOptionLabel={(v: Branch) => v.name || ''}
              getOptionValue={(v: Branch) => String(v.id)}
              placeholder="-- Select Vessel --"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">العلم (Flag)</label>
              <input type="text" value={form.flag} onChange={e => setForm(prev => ({ ...prev, flag: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">جنسية المالك (Owner Nationality)</label>
              <input type="text" value={form.ownerNationality} onChange={e => setForm(prev => ({ ...prev, ownerNationality: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">من ميناء (From Port)</label>
              <input type="text" value={form.fromPort} onChange={e => setForm(prev => ({ ...prev, fromPort: e.target.value }))} className={inputClass} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">المتواجدة حاليا (Current Location)</label>
              <input type="text" value={form.currentLocation} onChange={e => setForm(prev => ({ ...prev, currentLocation: e.target.value }))} className={inputClass} />
            </div>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Select Employees ({form.selectedEmployeeIds.length} selected)</h3>
            {batches.length > 0 && (
              <div className="mb-2">
                <AsyncSelect
                  label="دفعة الـ Crew Change (الوحدة + تاريخ النزول) — اختر لتحديد نفس المجموعة:"
                  name="crewBatch"
                  value={selectedBatch ? `${selectedBatch.date}|${selectedBatch.vesselId}` : ''}
                  onChange={() => { /* selection handled via onSelectItem */ }}
                  onSelectItem={(b: CrewChangeBatch | null) => { if (b) selectBatch(b); }}
                  fetchOptions={fetchBatchOptions}
                  getOptionLabel={(b: CrewChangeBatch) => `${b.label} (${b.employeeIds.length})`}
                  getOptionValue={(b: CrewChangeBatch) => `${b.date}|${b.vesselId}`}
                  placeholder="— اختر دفعة (Select crew batch) —"
                />
              </div>
            )}
            {autoFilledFromCrewChange && form.selectedEmployeeIds.length > 0 && (
              <p className="mb-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1">
                تم اختيار {form.selectedEmployeeIds.length} بحار تلقائياً من آخر Crew Change (Sign Off) — يمكنك التعديل يدوياً.
              </p>
            )}
            <input
              type="text"
              value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)}
              placeholder="ابحث بالاسم أو الوظيفة أو الرقم القومي… (Search employees)"
              className={`${inputClass} mb-2`}
            />
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md divide-y">
              {visibleEmployees.length === 0 && <div className="p-3 text-sm text-gray-500 text-center">{employees.length === 0 ? 'No employees found' : 'No matches'}</div>}
              {visibleEmployees.map(emp => (
                <label key={emp.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input type="checkbox" checked={form.selectedEmployeeIds.includes(emp.id)} onChange={() => handleToggleEmployee(emp.id)} className="h-4 w-4 text-blue-600 border-gray-300 rounded mr-3" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-gray-900">{arabicEmployeeName(emp)}</span>
                    <span className="text-xs text-gray-500 ml-2">({emp.position})</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onGeneratePreview} disabled={isPrinting || !form.passportOffice || form.selectedEmployeeIds.length === 0} className="flex-1 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">{isPrinting ? 'Generating…' : 'Generate'}</button>
            {generatedPreview && <button onClick={onPrintLetter} className="flex-1 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700">Print</button>}
          </div>
        </div>
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 border-b pb-2 mb-4">Preview</h2>
          {generatedPreview ? (
            <div className="border rounded-md overflow-auto bg-white" style={{ minHeight: 400 }}>
              <iframe srcDoc={generatedPreview} title="Letter Preview" className="w-full border-0" style={{ minHeight: 600 }} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 py-20" dir="rtl">
              <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm">قم بتعبئة البيانات واضغط &quot;Generate&quot; لمعاينة الخطاب</p>
              {selectedEmps.length > 0 && <p className="text-xs mt-1">{selectedEmps.length} بحار مختار</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RaaftGeneratorForm;
